import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import moment from 'moment'
// Define the proper activity log entry interface for frontend use
interface ActivityLogEntry {
  type: 'created' | 'updated'
  date: Date | string // Can be Date object or ISO string
  userEmail?: string
  userObjectId?: string
  reason?: string
  changes?: {
    field: string
    oldValue: any // These need to be any since they can be strings, booleans, numbers, etc.
    newValue: any
  }[]
}

interface ActivityLogModalProps {
  open: boolean
  onClose: () => void
  activityLog: ActivityLogEntry[] | null | undefined
  technicianName: string
  selectedDate: Date
}

export default function ActivityLogModal({
  open,
  onClose,
  activityLog,
  technicianName,
  selectedDate,
}: ActivityLogModalProps) {
  if (!activityLog || activityLog.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Activity Log - {technicianName}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
          <Typography variant="subtitle2" color="textSecondary">
            {moment(selectedDate).format('dddd, MMMM Do, YYYY')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box p={3} textAlign="center" color="textSecondary">
            <Typography>No activity log entries found for this day setting.</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  // Sort activity log by date (newest first)
  const sortedActivityLog = [...activityLog].sort((a, b) => {
    const dateA = new Date(a.date)
    const dateB = new Date(b.date)
    return dateB.getTime() - dateA.getTime()
  })

  const formatFieldName = (field: string): string => {
    const fieldMap: Record<string, string> = {
      workDay: 'Work Day',
      startTime: 'Start Time',
      endTime: 'End Time',
      lunchDuration: 'Lunch Duration',
      reason: 'Reason',
    }
    return fieldMap[field] || field
  }

  const formatFieldValue = (field: string, value: any): string => {
    if (value === null || value === undefined) return 'Not set'
    
    switch (field) {
      case 'workDay':
        return value ? 'Working' : 'Off'
      case 'startTime':
      case 'endTime':
        return value
      case 'lunchDuration':
        return `${value} min`
      default:
        return String(value)
    }
  }

  const renderChanges = (changes?: ActivityLogEntry['changes']) => {
    if (!changes || changes.length === 0) return null

    return (
      <Box>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom fontWeight={500}>
          Changes:
        </Typography>
        {changes.map((change, index: number) => (
          <Box key={index} mb={0.5}>
            <Typography variant="body2" component="div">
              <strong>{formatFieldName(change.field)}:</strong>{' '}
              <span style={{ color: '#d32f2f' }}>{formatFieldValue(change.field, change.oldValue)}</span>
              {' → '}
              <span style={{ color: '#2e7d32' }}>{formatFieldValue(change.field, change.newValue)}</span>
            </Typography>
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Activity Log - {technicianName}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
        <Typography variant="subtitle2" color="textSecondary">
          {moment(selectedDate).format('dddd, MMMM Do, YYYY')} • Most recent first
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box 
          position="relative"
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            backgroundColor: '#fafafa',
            maxHeight: '400px',
            overflowY: 'auto',
            p: 2,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#a8a8a8',
            }
          }}
        >
          {/* Visual timeline indicator */}
          <Box
            position="absolute"
            left={4}
            top={16}
            bottom={16}
            width={4}
            sx={{
              background: 'linear-gradient(to bottom, #2e7d32 0%, #4caf50 30%, #81c784 70%, #c8e6c9 100%)',
              borderRadius: 2
            }}
          />
          
          {sortedActivityLog.map((entry, index) => (
            <Box
              key={index}
              p={2}
              mb={1.5}
              ml={2}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: index === 0 ? '#f8f9fa' : '#ffffff',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -18,
                  top: 0,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === 0 ? '#2e7d32' : (index === sortedActivityLog.length - 1 ? '#c8e6c9' : '#4caf50'),
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            >
              {/* Header with badge-like date */}
              <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={moment(entry.date).format('MMM D, YYYY [at] h:mm A')}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                  {index === 0 && (
                    <Chip
                      label="LATEST"
                      size="small"
                      sx={{ 
                        backgroundColor: '#e8f5e8', 
                        color: '#2e7d32',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  )}
                  {index === sortedActivityLog.length - 1 && sortedActivityLog.length > 1 && (
                    <Chip
                      label="FIRST"
                      size="small"
                      sx={{ 
                        backgroundColor: '#f3e5f5', 
                        color: '#7b1fa2',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Single column layout: Changes, then Reason with user underneath */}
              <Box>
                {/* Changes section */}
                {entry.type === 'updated' && renderChanges(entry.changes)}
                
                {/* Reason section */}
                {entry.reason && (
                  <Box mt={entry.type === 'updated' && entry.changes?.length ? 2 : 0}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom fontWeight={500}>
                      Reason:
                    </Typography>
                    <Box 
                      p={1.5} 
                      sx={{ 
                        backgroundColor: '#f8f9fa', 
                        border: '1px solid #e9ecef', 
                        borderRadius: 1,
                        fontStyle: 'italic'
                      }}
                    >
                      <Typography variant="body2" color="textPrimary">
                        "{entry.reason}"
                      </Typography>
                    </Box>
                    {entry.userEmail && (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'normal' }}>
                        by {entry.userEmail}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* Show user even if no reason (fallback) */}
                {!entry.reason && entry.userEmail && (
                  <Box mt={entry.type === 'updated' && entry.changes?.length ? 2 : 0}>
                    <Typography variant="caption" color="textSecondary">
                      by {entry.userEmail}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}